import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { formatDate, retrieveFromLocalStorage } from '../../../../utils/util';
import { BUYER, SELLER } from '../../../../utils/constants';

import { requestRequestAssistance } from '../../../../api/contract/contract.actions';

import Contract from '../../../common/contract/Contract';
import Button from '../../../common/button/Button';
import DealCommunication from './deal-communication/DealCommunication';
import Tooltip from '../../../common/tooltip/ToolTip';
import Modal from '../../../common/modal/Modal';

import { ReactComponent as OpenNewIcon } from '../../../../resources/icons/open-new.svg';

import './deal.scss';

const Deal = ({
  contract,
  profile,
  chats,
  amendContract,
  updateContractStatus,
  downloadDocument,
  cancelContract,
  sendMessage,
  messageSentOnSuccess,
  openChat,
}) => {
  const [companyName, setCompanyName] = useState('-');
  const [memberSince, setMemberSince] = useState('-');
  const [role, setRole] = useState(null);
  const [showCommunication, setShowCommunication] = useState(false);
  const [assistanceRequiredBool, setAssistanceRequiredBool] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);

  const showPopup = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const personExternalReference = retrieveFromLocalStorage('personExternalReference');

  useEffect(() => {
    if (contract) {
      const { organisationExternalReference } = profile;
      const { buyer, seller, assistanceRequired } = contract;

      setAssistanceRequiredBool(assistanceRequired ? true : false);

      if (buyer && buyer.externalReference === organisationExternalReference) {
        setRole(BUYER);
        setCompanyName(seller.companyName);
        setMemberSince(formatDate(seller.memberSince));
      } else if (seller && seller.externalReference === organisationExternalReference) {
        setRole(SELLER);
        setCompanyName(buyer.companyName);
        setMemberSince(formatDate(buyer.memberSince));
      }
    }
    if (openChat) {
      setShowCommunication(true);
    }
  }, [contract, profile, openChat]);

  const handleLinkClick = () => {
    if (contract.product.type === SELLER) {
      navigate(`/market/product-listing/${contract.product.originalProductExternalReference}`);
    } else {
      navigate(`/market/buyer-listing/${contract.product.originalProductExternalReference}`);
    }
  };

  const resolveAssistance = () => {
    const { dealNumber } = contract;
    const data = {
      dealNumber,
      personExternalReference,
    };
    dispatch(requestRequestAssistance({ data, onSuccess: () => messageSentOnSuccess() }));
  };

  const handleButtonClick = () => {
    if (!showCommunication) {
      if (showPopup.current) {
        setShowWarningPopup(true);
      } else {
        setShowCommunication(true);
      }
    } else {
      setShowCommunication(false);
      showPopup.current = false;
    }
  };

  const handleReturnClick = () => {
    setShowWarningPopup(false);
  };

  const handleContinueClick = () => {
    setShowWarningPopup(false);
    setShowCommunication(true);
  };

  const renderContracts = () => {
    if (!contract) return null;

    const { product, versions, buyer, seller, involvedPeople, dealNumber } = contract;

    if (versions.length === 1) {
      return (
        <Contract
          dealNumber={dealNumber}
          currentVersion={versions[0]}
          product={product}
          role={role}
          buyer={buyer}
          seller={seller}
          involvedPeople={involvedPeople}
          amendContract={amendContract}
          cancelContract={cancelContract}
          showPopup={showPopup}
          latest
        />
      );
    }

    const sortedVersions = [...versions].sort((versionOne, versionTwo) => {
      const creationTimeOne = new Date(versionOne.creationDate).getTime();
      const creationTimeTwo = new Date(versionTwo.creationDate).getTime();

      return creationTimeTwo - creationTimeOne;
    });

    return sortedVersions.map((version, index) => {
      if (version.version === '0') return null;

      return (
        <Contract
          key={version.creationDate}
          dealNumber={dealNumber}
          currentVersion={version}
          previousVersion={sortedVersions[index + 1]}
          product={product}
          role={role}
          buyer={buyer}
          seller={seller}
          involvedPeople={involvedPeople}
          amendContract={amendContract}
          updateContractStatus={updateContractStatus}
          downloadDocument={downloadDocument}
          latest={index === 0}
          showPopup={showPopup}
        />
      );
    });
  };

  const renderCommunication = () => {
    return (
      <DealCommunication
        chats={chats}
        sendMessage={sendMessage}
        assistanceRequired={assistanceRequiredBool}
        resolveAssistance={resolveAssistance}
        messageSentOnSuccess={messageSentOnSuccess}
      />
    );
  };

  return (
    <>
      <section className="deal" data-testid="deal">
        <section className="deal__information">
          <h1 className="deal__title">Deal Room</h1>
          <button className="deal__back-button" onClick={() => navigate('/shop/deal-room')}>
            {'<'} Back
          </button>
          <div className="deal__information__deal-number">
            <p className="deal__information__deal-number__label">Deal Number</p>
            <p className="deal__information__deal-number__value">#{contract?.dealNumber}</p>
          </div>
          <div className="deal__information__trading-party">
            <p className="deal__information__trading-party__title">Trading Party</p>
            <p className="deal__information__trading-party__label">Company Name</p>
            <p className="deal__information__trading-party__value">{companyName}</p>
            <p className="deal__information__trading-party__label">Member Since</p>
            <p className="deal__information__trading-party__value">{memberSince}</p>
          </div>
          {contract?.facilitator && (
            <div className="deal__information__facilitator">
              <div className="deal__information__facilitator__container">
                <p className="deal__information__facilitator__label">ATP Facilitator</p>
                <div className="deal__information__facilitator__tooltip">
                  <Tooltip
                    text={'Someone who assists in finalizing a deal and handles any related matters.'}
                    maxWidth={245}
                    toolTipLeft={true}
                  />
                </div>
              </div>
              <p className="deal__information__facilitator__value">{contract.facilitator?.name}</p>
            </div>
          )}
          <button className="deal__information__button" onClick={handleLinkClick}>
            <p>Listing Offer</p>
            <OpenNewIcon />
          </button>
        </section>
        <section className="deal__contracts">
          <div className="deal__contracts__header">
            <h1 className="deal__title">Deal #{contract?.dealNumber}</h1>
            <Button onClick={handleButtonClick}>{showCommunication ? 'BACK TO DEAL' : 'CHAT'}</Button>
          </div>
          {showCommunication ? renderCommunication() : renderContracts()}
        </section>
      </section>

      <Modal show={showWarningPopup} onClose={handleReturnClick}>
        <section className="deal__warning-popup">
          <p className="deal__warning-popup__text">You are about navigate away from this page.</p>
          <p className="deal__warning-popup__text">All Changes will be lost.</p>

          <div className="deal__warning-popup__buttons">
            <Button className="deal__warning-popup__button" onClick={handleReturnClick} secondary>
              Return
            </Button>
            <Button className="deal__warning-popup__button" onClick={handleContinueClick}>
              Continue
            </Button>
          </div>
        </section>
      </Modal>
    </>
  );
};

Deal.propTypes = {
  contract: PropTypes.shape({}),
  chats: PropTypes.arrayOf(PropTypes.shape({})),
  profile: PropTypes.shape({}),
  amendContract: PropTypes.func.isRequired,
  updateContractStatus: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  cancelContract: PropTypes.func.isRequired,
  sendMessage: PropTypes.func.isRequired,
  messageSentOnSuccess: PropTypes.func.isRequired,
  openChat: PropTypes.bool,
};

Deal.defaultProps = {
  contract: undefined,
  chats: [],
  profile: undefined,
  openChat: false,
};

export default Deal;
